import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { api } from '@/lib/api'

interface Member {
  id: number
  name: string
  email: string
  clio_user_id: string
  manual: boolean
}

interface ClioUser {
  id: string
  name: string
  email: string
}

interface MemberList {
  members: Member[]
  clio_users: ClioUser[]
}

export interface ClioTimeError {
  membre?: string
  clio?: string
}

/**
 * Clio panel: which Clio account each Callizy member's call time goes to.
 * Pre-filled by the server (the member who connected Clio, then by email),
 * editable here. "No Clio account": the call is logged without time.
 */
export function ClioMembresTemps({ companyId, timeError }: { companyId: number | undefined; timeError?: ClioTimeError | null }) {
  const { t } = useTranslation()
  const tx = (cle: string, options?: Record<string, unknown>) => t(`manager.clio.membres.${cle}`, options)
  const queryClient = useQueryClient()
  const key = ['clio-members', companyId]
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<MemberList>(`/api/companies/${companyId}/clio/users/`)).data,
    enabled: Boolean(companyId),
    staleTime: 60 * 1000,
    retry: false,
  })
  const choose = useMutation({
    mutationFn: async (body: { member_id: number; clio_user_id: string }) =>
      (await api.patch<MemberList>(`/api/companies/${companyId}/clio/users/`, body)).data,
    onSuccess: (list) => {
      queryClient.setQueryData(key, list)
      toast.success(tx('enregistre'))
    },
    onError: () => toast.error(tx('erreur')),
  })

  return (
    <div className="px-6 py-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('titre')}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{tx('texte')}</p>
      </div>
      {timeError?.membre || timeError?.clio ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          {tx('refus', { clio: timeError.clio || timeError.membre, membre: timeError.membre || timeError.clio })}
        </p>
      ) : null}
      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {tx('chargement')}
        </p>
      ) : isError || !data ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{tx('illisible')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.members.map((member) => (
            <li key={member.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3">
              <label htmlFor={`clio-member-${member.id}`} className="min-w-0">
                <span className="block text-sm font-medium text-slate-900 dark:text-white truncate">{member.name}</span>
                {member.email ? (
                  <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">{member.email}</span>
                ) : null}
              </label>
              <select
                id={`clio-member-${member.id}`}
                value={member.clio_user_id}
                onChange={(e) => choose.mutate({ member_id: member.id, clio_user_id: e.target.value })}
                disabled={choose.isPending}
                className="w-full sm:w-72 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-base sm:text-sm text-slate-900 dark:text-white disabled:opacity-60"
              >
                <option value="">{tx('aucun')}</option>
                {data.clio_users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                    {user.email ? ` (${user.email})` : ''}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
